import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import BackToTop from './BackToTop';
import HeroAnimation from './HeroAnimation';
import ShiftTeaser from './ShiftTeaser';
import ContactModal from './ContactModal';
import SEO from './SEO';
import { useTheme } from '../context/ThemeContext';

// Smooth Section Reveal Component
const SectionReveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div 
      ref={ref} 
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
      }}
    >
      {children}
    </div>
  );
};

// ==================== INTERACTIVE SERVICE CARD ====================
const ServiceCard = ({ offering, index, totalCards, isDarkMode, mousePosition, isAnyHovered, setHoveredIndex, hoveredIndex }) => {
  const cardRef = useRef(null);
  const [localMouse, setLocalMouse] = useState({ x: 0.5, y: 0.5 });
  const [isHovered, setIsHovered] = useState(false);
  
  const textClass = isDarkMode ? 'text-[#F2F2F2]' : 'text-[#1A1A1A]';
  const subtextClass = isDarkMode ? 'text-[#999999]' : 'text-[#555555]';
  const cardBgClass = isDarkMode ? 'bg-[#141414]' : 'bg-white';
  const borderClass = isDarkMode ? 'border-white/10' : 'border-[#E5E5E5]';

  // Track mouse position within card
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setLocalMouse({ x, y });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setLocalMouse({ x: 0.5, y: 0.5 });
    setHoveredIndex(null);
  };

  // Calculate fan-out rotation based on card position
  const getFanRotation = () => {
    if (!isAnyHovered) return 0;
    if (isHovered) return 0;
    
    // Fan out neighboring cards
    const diff = index - hoveredIndex;
    if (Math.abs(diff) > 2) return 0;
    
    // Cards fan outward from hovered card
    const rotationAmount = diff * 2;
    return rotationAmount;
  };

  // Calculate 3D transform based on mouse position
  const getTransform = () => {
    if (!isHovered) {
      const fanRotation = getFanRotation();
      const scale = isAnyHovered && !isHovered ? 0.98 : 1;
      return `
        perspective(1000px)
        rotateY(${fanRotation}deg)
        scale(${scale})
        translateY(${isAnyHovered && !isHovered ? '5px' : '0px'})
      `;
    }
    
    // 3D tilt effect on hover
    const rotateX = (localMouse.y - 0.5) * -10;
    const rotateY = (localMouse.x - 0.5) * 10;
    
    return `
      perspective(1000px)
      rotateX(${rotateX}deg)
      rotateY(${rotateY}deg)
      scale(1.03)
      translateY(-10px)
    `;
  };

  // Gradient spotlight effect
  const getGradientStyle = () => {
    if (!isHovered) return {};
    
    const x = localMouse.x * 100;
    const y = localMouse.y * 100;
    
    return {
      background: `
        radial-gradient(
          600px circle at ${x}% ${y}%,
          ${offering.color}15,
          transparent 40%
        )
      `,
    };
  };

  return (
    <div
      ref={cardRef}
      className={`
        relative group cursor-pointer
        rounded-3xl border-2 ${borderClass}
        transition-all duration-500 ease-out
        ${isHovered ? 'z-20' : 'z-10'}
      `}
      style={{
        transform: getTransform(),
        transformStyle: 'preserve-3d',
        boxShadow: isHovered 
          ? `0 25px 50px -12px ${offering.color}30, 0 0 0 1px ${offering.color}40`
          : isDarkMode 
            ? '0 4px 20px rgba(0,0,0,0.3)'
            : '0 4px 20px rgba(0,0,0,0.08)',
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Animated gradient background */}
      <div 
        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={getGradientStyle()}
      />
      
      {/* Glowing border on hover */}
      <div 
        className={`
          absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100
          transition-opacity duration-500
        `}
        style={{
          background: `linear-gradient(135deg, ${offering.color}20, transparent, ${offering.color}10)`,
        }}
      />

      {/* Card content */}
      <div className={`relative ${cardBgClass} rounded-3xl p-8 md:p-10 h-full overflow-hidden`}>
        {/* Floating accent shapes */}
        <div 
          className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-20 blur-2xl transition-all duration-700 group-hover:opacity-40 group-hover:scale-150"
          style={{ backgroundColor: offering.color }}
        />
        <div 
          className="absolute -bottom-10 -left-10 w-24 h-24 rounded-full opacity-10 blur-xl transition-all duration-700 group-hover:opacity-30 group-hover:scale-125"
          style={{ backgroundColor: offering.color }}
        />

        {/* Number badge */}
        <div className="relative mb-8">
          <span 
            className="inline-flex items-center justify-center text-sm font-mono px-4 py-2 rounded-xl transition-all duration-300 group-hover:scale-110"
            style={{ 
              backgroundColor: isHovered ? `${offering.color}25` : `${offering.color}15`, 
              color: offering.color,
              boxShadow: isHovered ? `0 4px 20px ${offering.color}30` : 'none'
            }}
          >
            {offering.number}
          </span>
        </div>

        {/* Title with animated underline */}
        <div className="relative mb-6 overflow-visible">
          <h3 
            className={`headline-xl text-2xl md:text-3xl lg:text-4xl transition-colors duration-300 ${isHovered ? 'text-[#F6CD17]' : textClass}`}
          >
            {offering.title}
          </h3>
          {/* Animated underline */}
          <div 
            className="absolute -bottom-2 left-0 h-[3px] rounded-full transition-all duration-500 ease-out"
            style={{ 
              width: isHovered ? '60%' : '0%',
              backgroundColor: offering.color,
              boxShadow: isHovered ? `0 0 10px ${offering.color}` : 'none'
            }}
          />
        </div>

        {/* Subtitle */}
        <p className={`${textClass} font-medium mb-5 text-lg transition-all duration-300 ${isHovered ? 'translate-x-2' : ''}`}>
          {offering.subtitle}
        </p>

        {/* Description */}
        <p className={`${subtextClass} leading-relaxed transition-all duration-300 ${isHovered ? 'translate-x-1 opacity-100' : 'opacity-80'}`}>
          {offering.description}
        </p>

        {/* Hover indicator arrow */}
        <div 
          className={`
            absolute bottom-8 right-8 w-12 h-12 rounded-full
            flex items-center justify-center
            transition-all duration-500 ease-out
            ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}
          `}
          style={{ 
            backgroundColor: `${offering.color}20`,
            border: `2px solid ${offering.color}40`
          }}
        >
          <svg 
            className="w-5 h-5 transition-transform duration-300"
            style={{ 
              color: offering.color,
              transform: isHovered ? 'translateX(3px)' : 'translateX(0)'
            }}
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

const FlinchLanding = () => {
  const { isDarkMode } = useTheme();
  const heroRef = useRef(null);
  const [heroAnimationComplete, setHeroAnimationComplete] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Track scroll for fade effects
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track global mouse position
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const bgClass = isDarkMode ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]';
  const textClass = isDarkMode ? 'text-[#F2F2F2]' : 'text-[#1A1A1A]';
  const subtextClass = isDarkMode ? 'text-[#999999]' : 'text-[#555555]';
  const cardBgClass = isDarkMode ? 'bg-[#141414]' : 'bg-white';
  const borderClass = isDarkMode ? 'border-white/10' : 'border-[#E5E5E5]';

  const offerings = [
    {
      number: '01',
      title: 'Strategy & Positioning',
      subtitle: 'Human-led clarity.',
      description: 'We define what your brand stands for, who it\'s for, and how it should show up — so decisions are confident and messaging stays aligned as you scale.',
      color: '#F6CD17'
    },
    {
      number: '02',
      title: 'Content & Creative',
      subtitle: 'AI-powered execution, human-shaped.',
      description: 'Intelligent systems help us move fast and work at scale. Human judgment shapes tone, nuance, and intent — so the work sounds like you, not a template.',
      color: '#F6CD17'
    },
    {
      number: '03',
      title: 'Analytics & Optimisation',
      subtitle: 'Data-informed, judgment-driven.',
      description: 'Modern analysis surfaces what\'s working. People decide what to double down on — refining direction and improving outcomes over time.',
      color: '#F6CD17'
    },
    {
      number: '04',
      title: 'Modern Capabilities & Systems',
      subtitle: 'Speed without losing control.',
      description: 'AI-enhanced workflows reduce friction and improve turnaround. Human decision-making keeps quality, taste, and priorities intact.',
      color: '#F6CD17'
    }
  ];

  return (
    <div className={`min-h-screen ${bgClass} ${textClass} transition-colors duration-300`}>
      {/* SEO */}
      <SEO 
        title="Strategic Marketing Partner"
        description="Streamline your marketing with AI-powered efficiency and human strategy. FLINCH delivers clarity, speed, and results that drive real business growth."
        canonical="/"
        isHomepage={true}
      />
      
      {/* ===== HEADER ===== */}
      <Header />
      <BackToTop />
      
      {/* Contact Modal */}
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />

      {/* ===== HERO SECTION ===== */}
      <section 
        ref={heroRef}
        className="min-h-screen flex flex-col justify-center pt-24 pb-16 px-6 md:px-8 relative overflow-hidden"
      >
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className={`absolute top-1/4 -right-32 w-96 h-96 rounded-full blur-[100px] ${
              isDarkMode ? 'bg-[#F6CD17]/10' : 'bg-[#F6CD17]/5'
            }`}
          />
          <div 
            className={`absolute bottom-1/4 -left-32 w-96 h-96 rounded-full blur-[100px] ${
              isDarkMode ? 'bg-[#35392F]/20' : 'bg-[#35392F]/5'
            }`}
          />
        </div>

        {/* Hero Content */}
        <div className="max-w-6xl mx-auto w-full relative z-10 text-center">
          {/* Animated Hero Headlines */}
          <div className="mb-8 md:mb-12">
            <HeroAnimation onComplete={() => setHeroAnimationComplete(true)} />
          </div>
          
          {/* Subtext - appears after animation */}
          <div 
            className={`transition-all duration-700 ${heroAnimationComplete ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '0.3s' }}
          >
            <p className={`${subtextClass} text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto`}>
              Your strategic marketing partner to bring clarity to chaos, personality to content, and outcomes that move the needle.
            </p>

            {/* Hero CTA Button */}
            <button 
              onClick={() => setIsContactOpen(true)}
              className="glass-button-primary inline-flex items-center gap-2"
            >
              Talk to us
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* ===== THE SHIFT TEASER - STANDOUT SECTION ===== */}
      <ShiftTeaser />

      {/* ===== OFFERINGS SECTION - INTERACTIVE CARDS ===== */}
      <section id="offerings" className="py-20 md:py-32 px-6 md:px-8 overflow-visible">
        <div className="max-w-7xl mx-auto">
          <SectionReveal>
            <span className={`text-xs uppercase tracking-widest ${subtextClass} block mb-4`}>
              WHAT WE DO
            </span>
            <h2 className={`headline-xl text-4xl md:text-5xl lg:text-6xl ${textClass} mb-16`}>
              Our Capabilities
            </h2>
          </SectionReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {offerings.map((offering, index) => (
              <SectionReveal key={offering.number} delay={index * 100}>
                <ServiceCard 
                  offering={offering}
                  index={index}
                  totalCards={offerings.length}
                  isDarkMode={isDarkMode}
                  mousePosition={mousePosition}
                  isAnyHovered={hoveredIndex !== null}
                  setHoveredIndex={setHoveredIndex}
                  hoveredIndex={hoveredIndex}
                />
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== METHOD SECTION (Simplified) ===== */}
      <SectionReveal>
        <section id="method" className={`py-20 md:py-28 px-6 md:px-8 ${isDarkMode ? 'bg-[#0D0D0D]' : 'bg-white'}`}>
          <div className="max-w-6xl mx-auto">
            <span className={`text-xs uppercase tracking-widest ${subtextClass} block mb-6`}>
              HOW WE WORK
            </span>
            <h2 className={`font-display text-3xl md:text-4xl lg:text-5xl mb-12 md:mb-16 ${textClass}`}>
              The Flinch Method
            </h2>
            
            {/* Simplified Content */}
            <div className="max-w-2xl">
              <p className={`text-xl md:text-2xl ${textClass} leading-relaxed mb-4`}>
                Modern tools help us move faster.
              </p>
              <p className={`text-xl md:text-2xl ${textClass} leading-relaxed mb-4`}>
                Clear thinking helps us move in the right direction.
              </p>
              <p className={`text-xl md:text-2xl ${textClass} leading-relaxed font-medium mb-8`}>
                <span className="text-[#F6CD17]">We bring both.</span>
              </p>
              
              {/* Read More Button */}
              <Link 
                to="/shift#flinch-method" 
                className="glass-button-primary inline-flex items-center gap-2"
              >
                Read more
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 8H13M13 8L9 4M13 8L9 12" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </SectionReveal>

      {/* ===== INSIGHTS TEASER ===== */}
      <SectionReveal>
        <section className={`py-20 md:py-28 px-6 md:px-8 border-t ${borderClass}`}>
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
              <div>
                <span className={`text-xs uppercase tracking-widest ${subtextClass} block mb-4`}>
                  INSIGHTS
                </span>
                <h2 className={`font-display text-3xl md:text-4xl ${textClass}`}>
                  Latest Thinking
                </h2>
              </div>
              <Link 
                to="/insights" 
                className={`${subtextClass} hover:text-[#F6CD17] transition-colors text-sm mt-4 md:mt-0 inline-flex items-center gap-2 group`}
              >
                View all insights
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 16 16" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="1.5"
                  className="transition-transform duration-300 group-hover:translate-x-1"
                >
                  <path d="M3 8H13M13 8L9 4M13 8L9 12" />
                </svg>
              </Link>
            </div>
            
            {/* Preview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link 
                to="/insights/1" 
                className={`group p-8 rounded-2xl border ${borderClass} ${cardBgClass} hover:border-[#F6CD17]/30 transition-all duration-300`}
              >
                <span className="text-xs font-medium uppercase tracking-wider px-3 py-1 rounded-full bg-[#F6CD17]/10 text-[#F6CD17] mb-4 inline-block">
                  Personal Branding
                </span>
                <h3 className={`font-display text-xl md:text-2xl ${textClass} mb-3 group-hover:text-[#F6CD17] transition-colors`}>
                  Why Your Personal Brand is Your Most Valuable Asset
                </h3>
                <p className={`${subtextClass} text-sm`}>Dec 15, 2024 · 5 min read</p>
              </Link>
              
              <Link 
                to="/insights/2" 
                className={`group p-8 rounded-2xl border ${borderClass} ${cardBgClass} hover:border-[#35392F]/30 transition-all duration-300`}
              >
                <span className="text-xs font-medium uppercase tracking-wider px-3 py-1 rounded-full bg-[#35392F]/10 text-[#35392F] mb-4 inline-block">
                  Strategy
                </span>
                <h3 className={`font-display text-xl md:text-2xl ${textClass} mb-3 group-hover:text-[#35392F] transition-colors`}>
                  The Art of Saying No: How Constraints Drive Excellence
                </h3>
                <p className={`${subtextClass} text-sm`}>Dec 10, 2024 · 7 min read</p>
              </Link>
            </div>
          </div>
        </section>
      </SectionReveal>

      {/* ===== CTA SECTION ===== */}
      <SectionReveal>
        <section id="contact" className={`py-20 md:py-28 px-6 md:px-8 ${isDarkMode ? 'bg-[#0D0D0D]' : 'bg-[#1A1A1A]'}`}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white mb-8">
              Ready to move forward?
            </h2>
            <button 
              onClick={() => setIsContactOpen(true)}
              className="glass-button-light inline-flex items-center gap-2"
            >
              Talk to us
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" />
              </svg>
            </button>
          </div>
        </section>
      </SectionReveal>

      {/* ===== FOOTER ===== */}
      <Footer />
    </div>
  );
};

export default FlinchLanding;
